/**
 * PromptEditorPage — Prompt 版本管理编辑器
 *
 * Features:
 * - Left: Prompt key list (e.g. email-parsing.system, job-creation.user)
 * - Right: Version editor with syntax highlighting, variable tokens, model params
 * - Version history panel (activate, rollback, compare)
 * - Metrics view (token usage, quality scores)
 *
 * API:
 *   GET    /training/prompts/:key/active
 *   GET    /training/prompts/:key/versions
 *   POST   /training/prompts/:key/versions      → create draft
 *   POST   /training/prompts/:key/activate       → activate version
 *   POST   /training/prompts/:key/rollback       → rollback
 *   PATCH  /training/prompts/versions/:id/metrics
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  FileText,
  Save,
  RotateCcw,
  CheckCircle,
  Clock,
  Loader2,
  Plus,
  History,
  Play,
  Zap,
  Search,
  Settings,
  ChevronRight,
  AlertCircle,
  Bot,
  Send,
  Sparkles,
  User,
  CheckCheck,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface PromptVersion {
  id: string;
  prompt_key: string;
  version: number;
  content: string;
  status: 'active' | 'draft' | 'archived';
  variables: string[];
  target_model: string | null;
  max_tokens: number | null;
  temperature: number | null;
  created_by: string | null;
  change_note: string | null;
  parent_id: string | null;
  total_uses: number;
  avg_quality_score: number | null;
  created_at: string;
}

interface AssistMessage {
  role: 'user' | 'assistant';
  content: string;
  suggestedPrompt?: string;   // set when assistant proposes a new prompt
  applied?: boolean;          // true after user clicks Apply
  createdAt: string;
}

// Well-known prompt keys — organized by category
const PROMPT_KEYS = [
  // ── Skills ────────────────────────────────────────────────────────────────
  { key: 'email-parsing.system',    labelKey: 'admin.prompt.emailParsingSystem',    skill: 'email-parsing',    category: 'Skills' },
  { key: 'email-parsing.user',      labelKey: 'admin.prompt.emailParsingUser',      skill: 'email-parsing',    category: 'Skills' },
  { key: 'job-creation.system',     labelKey: 'admin.prompt.jobCreationSystem',     skill: 'job-creation',     category: 'Skills' },
  { key: 'job-creation.user',       labelKey: 'admin.prompt.jobCreationUser',       skill: 'job-creation',     category: 'Skills' },
  { key: 'job-validation.system',   labelKey: 'admin.prompt.jobValidationSystem',   skill: 'job-validation',   category: 'Skills' },
  { key: 'hs-code.system',          labelKey: 'admin.prompt.hsCodeSystem',          skill: 'hs-code',          category: 'Skills' },
  { key: 'billing.system',          labelKey: 'admin.prompt.billingSystem',         skill: 'billing',          category: 'Skills' },
  { key: 'customer-inquiry.system', labelKey: 'admin.prompt.customerInquirySystem', skill: 'customer-inquiry', category: 'Skills' },
  { key: 'sea-import.system',       labelKey: 'admin.prompt.seaImportSystem',       skill: 'sea-import',       category: 'Skills' },
  { key: 'nvocc.system',            labelKey: 'admin.prompt.nvoccSystem',           skill: 'nvocc',            category: 'Skills' },
  { key: 'dg-validation.system',    labelKey: 'admin.prompt.dgValidationSystem',    skill: 'dg-validation',    category: 'Skills' },
  { key: 'compliance.system',       labelKey: 'admin.prompt.complianceSystem',      skill: 'compliance',       category: 'Skills' },

  // ── Agent Core ────────────────────────────────────────────────────────────
  { key: 'agent.router',      labelKey: 'admin.prompt.agentRouter',      skill: 'router',      category: 'Agent Core' },
  { key: 'agent.dev',         labelKey: 'admin.prompt.agentDev',         skill: 'dev',         category: 'Agent Core' },
  { key: 'agent.ops',         labelKey: 'admin.prompt.agentOps',         skill: 'ops',         category: 'Agent Core' },
  { key: 'agent.data',        labelKey: 'admin.prompt.agentData',        skill: 'data',        category: 'Agent Core' },
  { key: 'agent.coordinator', labelKey: 'admin.prompt.agentCoordinator', skill: 'coordinator', category: 'Agent Core' },
  { key: 'agent.safety',      labelKey: 'admin.prompt.agentSafety',      skill: 'safety',      category: 'Agent Core' },

  // ── Agent Domain ─────────────────────────────────────────────────────────
  { key: 'agent.inbox',   labelKey: 'admin.prompt.agentInbox',   skill: 'inbox',   category: 'Agent Domain' },
  { key: 'agent.billing', labelKey: 'admin.prompt.agentBilling', skill: 'billing', category: 'Agent Domain' },
  { key: 'agent.cs',      labelKey: 'admin.prompt.agentCs',      skill: 'cs',      category: 'Agent Domain' },

  // ── Provider Prompts ──────────────────────────────────────────────────────
  { key: 'system:anthropic', labelKey: 'admin.prompt.providerAnthropic', skill: 'provider', category: 'Provider' },
  { key: 'system:openai',    labelKey: 'admin.prompt.providerOpenai',    skill: 'provider', category: 'Provider' },
  { key: 'system:gemini',    labelKey: 'admin.prompt.providerGemini',    skill: 'provider', category: 'Provider' },
  { key: 'system:default',   labelKey: 'admin.prompt.providerDefault',   skill: 'provider', category: 'Provider' },

  // ── Safety ────────────────────────────────────────────────────────────────
  { key: 'global-safety.database',         labelKey: 'admin.prompt.safetyDatabase',        skill: 'safety', category: 'Safety' },
  { key: 'global-safety.tenant-isolation', labelKey: 'admin.prompt.safetyTenantIsolation', skill: 'safety', category: 'Safety' },
  { key: 'global-safety.sql',              labelKey: 'admin.prompt.safetySql',              skill: 'safety', category: 'Safety' },

  // ── Autonomous Behavior ───────────────────────────────────────────────────
  { key: 'autonomous.behavior', labelKey: 'admin.prompt.autonomousBehavior', skill: 'autonomous', category: 'Autonomous' },

  // ── Document Parsers ──────────────────────────────────────────────────────
  { key: 'document-parser.mbl',      labelKey: 'admin.prompt.docParserMbl',      skill: 'document-parser', category: 'Documents' },
  { key: 'document-parser.hbl',      labelKey: 'admin.prompt.docParserHbl',      skill: 'document-parser', category: 'Documents' },
  { key: 'document-parser.manifest', labelKey: 'admin.prompt.docParserManifest', skill: 'document-parser', category: 'Documents' },
  { key: 'document-parser.noa',      labelKey: 'admin.prompt.docParserNoa',      skill: 'document-parser', category: 'Documents' },
];

// Default starter templates for each prompt key (shown when no version exists)
const STARTER_TEMPLATES: Record<string, string> = {
  'email-parsing.system': `You are an expert logistics email parser.

Given a raw email (subject + body), extract structured shipment data:
- Booking reference / Job number
- Container number(s) and size/type
- Vessel / Voyage
- Port of Loading (POL) and Port of Discharge (POD)
- ETA / ETD dates
- Shipper / Consignee / Notify party
- Cargo description and weight
- Special instructions

Output as JSON. If a field is not found, set it to null.
Do NOT hallucinate data — only extract what is explicitly stated.

<variables>
{{tenant_name}} — The tenant company name
{{email_subject}} — Email subject line
{{email_body}} — Email body content
</variables>`,

  'email-parsing.user': `Parse the following email and extract shipment data:

Subject: {{email_subject}}
Body:
{{email_body}}`,

  'job-creation.system': `You are a logistics job creation assistant for {{tenant_name}}.

Given parsed shipment data (JSON), create a job record with:
- Job type (IMP/EXP/COL/RET/LCL/FCL)
- Parties (shipper, consignee, notify, haulier)
- Trip legs (pickup → port, port → delivery)
- Container details
- Key dates (ETA, ETD, cutoff)
- Reference numbers

Follow the tenant's job numbering convention.
Validate required fields before creation.
Return the complete job object as JSON.`,

  'job-creation.user': `Create a job from the following parsed data:

{{parsed_data}}`,

  'job-validation.system': `You are a logistics job validator.

Check the job record for:
1. Required fields completeness (container, dates, parties, locations)
2. Date logic (ETD < ETA, cutoff < ETD)
3. Container format validity (4 letters + 7 digits)
4. Port code validity (UN/LOCODE format)
5. Weight/volume reasonability
6. Party information completeness

Return a validation report with:
- status: "pass" | "warning" | "fail"
- issues: array of { field, severity, message }
- suggestions: array of recommended fixes`,

  'hs-code.system': `You are an HS/HTS code classification expert.

Given a cargo description, determine the most appropriate HS code(s):
- Analyze the goods description carefully
- Consider material, use, and form
- Provide 6-digit HS code minimum, 8-10 digit if possible
- Include duty rate information if known
- Flag any potential classification ambiguities

Output format:
{
  "primary_hs_code": "XXXX.XX",
  "description": "Official HS description",
  "confidence": 0.0-1.0,
  "alternatives": [...],
  "notes": "Classification reasoning"
}`,

  'billing.system': `You are a logistics billing calculator for {{tenant_name}}.

Given a completed job, calculate charges based on:
- Service rates (haulage, documentation, handling)
- Container size/type surcharges
- Depot charges and storage
- Port charges
- Per-trip and per-container rates
- Customer-specific rate agreements
- Markup rules (POB, fuel surcharge)

Apply the tenant's pricing rules and generate an invoice breakdown.
All amounts in the tenant's base currency.`,

  'customer-inquiry.system': `You are a customer service AI for {{tenant_name}}, a logistics company.

Handle customer inquiries about:
- Shipment status and ETA
- Document requests (BL, DO, SOA)
- Rate quotes
- Booking confirmations
- General logistics questions

Guidelines:
- Be professional and concise
- Provide accurate information from the system
- If unsure, escalate to human support
- Never share other customers' data
- Respond in the customer's language`,

  'sea-import.system': `You are a sea import operations specialist.

Process import shipment workflows:
1. Parse incoming manifest data
2. Create Master BL and House BL records
3. Link containers to shipments
4. Generate AP entries from debit notes
5. Track vessel ETA and container availability
6. Coordinate CFS operations for LCL cargo
7. Generate arrival notices

Follow Singapore port procedures and PSA requirements.`,

  'nvocc.system': `You are an NVOCC LCL consolidation specialist.

Handle LCL (Less than Container Load) operations:
1. Batch building — group compatible shipments into containers
2. Port resolution — validate POL/POD combinations
3. Commodity compatibility — check for DG conflicts
4. Space optimization — maximize container utilization
5. Risk scoring — assess consolidation risk factors
6. Rate calculation — apply LCL rate per CBM/ton

Ensure compliance with shipping line requirements and port regulations.`,

  'dg-validation.system': `You are a dangerous goods (DG) compliance specialist.

Validate DG declarations against:
- IMDG Code requirements
- UN number and proper shipping name
- Packing group and classification
- MSDS document completeness
- Segregation rules (incompatible goods)
- Packaging and labeling requirements
- Emergency procedures

Flag any non-compliance issues with severity levels.
Reference applicable regulations for each finding.`,

  'compliance.system': `You are a regulatory compliance checker for logistics operations.

Check shipments against:
1. SFA (Singapore Food Agency) — food/agricultural imports
2. SCDF (Singapore Civil Defence Force) — hazardous materials
3. AMS (Automated Manifest System) — US-bound cargo
4. Sanctions screening — denied parties list
5. Credit limit checks — customer credit assessment
6. License/permit requirements — controlled goods

Return a compliance report with:
- status: "clear" | "review_required" | "blocked"
- checks: array of { regulation, result, details }
- required_actions: array of next steps`,

  // ── Agent Core ─────────────────────────────────────────────────────────────
  'agent.router': `You are Boox Router Agent.

Route tasks to the correct sub-agents (Dev / Ops / Data / Coordinator).
Decompose requests, assess risk, and ensure task completion.

Risk levels: LOW → act directly | MEDIUM → proceed with caution | HIGH → output plan + await confirmation`,

  'agent.dev': `You are BooxDevAgent, a production coding agent.

Produce runnable, production-grade code artifacts.
Output complete code, not fragments.
Include: File Tree, Design Decisions, Code, How to Run, Limitations.`,

  'agent.ops': `You are BooxOpsAgent, an execution-focused operations agent.

Execute operational tasks step by step.
Maintain task state: pending/done/failed.
Always output: Current Task State Table, Next Action, Blockers.`,

  'agent.data': `You are BooxDataAgent, a data analysis agent.

Turn data into actionable insights.
Output: Observations, Insights, Risks, Recommended Action, Confidence.`,

  'agent.coordinator': `You are BooxCoordinatorAgent, a workflow orchestrator.

Coordinate multi-agent work.
Maintain Workflow Board: Backlog / In Progress / Done / Blockers.
Define acceptance criteria up front.`,

  'agent.safety': `You are BooxSafetyAgent, a safety and compliance checker.

Block dangerous operations and escalate when necessary.
Output: SAFETY_CHECK with Action, Risk Level, Permitted, Reason, Required Steps.`,

  // ── Agent Domain ───────────────────────────────────────────────────────────
  'agent.inbox': `You are BooxInboxAgent, the unified messaging intake agent.

Classify incoming messages (Quotation/Operations/Finance/CS/Sales).
Output: INTAKE_RESULT with Intent, Urgency, Entities, Suggested Action, Route To.`,

  'agent.billing': `You are BooxBillingAgent, the billing and invoicing specialist.

Generate accurate, explainable invoices.
Apply pricing policies correctly.
Output: INVOICE_DRAFT with Lines, Subtotal, Adjustments, Total, Confidence, Flags.`,

  'agent.cs': `You are BooxCSAgent, the customer service agent.

Handle customer inquiries with speed and accuracy.
Decide: auto_send / review_required / escalate.
Output: CS_RESPONSE with Issue Type, Suggested Response, Action, Escalation Reason.`,

  // ── Provider Prompts ───────────────────────────────────────────────────────
  'system:anthropic': `You are Boox Agent, an intelligent automation assistant for the Boox logistics platform.

<identity>
You help users manage logistics operations, automate workflows, and extract insights.
You are precise, professional, and action-oriented.
</identity>

<tone>
- Be concise and direct. Avoid filler phrases.
- Use markdown for formatting.
- Prefer action over explanation when the task is clear.
</tone>`,

  'system:openai': `You are Boox Agent, an intelligent automation assistant for the Boox logistics platform.

## Identity
You help users manage logistics operations, automate workflows, and extract insights.

## Guidelines
- Be concise and direct
- Use markdown formatting
- Prefer action over explanation`,

  'system:gemini': `You are Boox Agent, an intelligent automation assistant for the Boox logistics platform.

Your role is to help users manage logistics operations and automate workflows.

Guidelines:
1. Be concise and direct
2. Use markdown for formatting
3. Prefer action over lengthy explanations`,

  'system:default': `You are Boox Agent, an intelligent automation assistant for the Boox logistics platform.

Help users manage logistics operations, automate workflows, and extract insights.
Be concise, professional, and action-oriented.`,

  // ── Safety ─────────────────────────────────────────────────────────────────
  'global-safety.database': `## 🔴 DATABASE SAFETY RULES (MANDATORY)

THREE databases — STRICT isolation:
- GLOBAL DB: tenants, users, roles, skills, permissions
- TENANT DB: jobs, orders, invoices, customers (always filter by tenantId)
- LOG DB: api_logs, ai_logs, traces (append-only)

Before ANY write: identify data type → select correct DB → verify tenantId is set.`,

  'global-safety.tenant-isolation': `## 🔒 TENANT ISOLATION RULES

- ALWAYS include WHERE tenant_id = ? in queries
- NEVER query across multiple tenants
- NEVER expose one tenant's data to another`,

  'global-safety.sql': `## 🛡️ SQL GENERATION SAFETY

1. Use parameterized queries (never concatenate values)
2. Include LIMIT clause to prevent large result sets
3. Always add WHERE clause for UPDATE/DELETE operations
4. Prefer specific columns over SELECT *`,

  // ── Autonomous ─────────────────────────────────────────────────────────────
  'autonomous.behavior': `## 🤖 Autonomous Behavior Rules

You are a fully autonomous digital employee.

1. Never Stop Prematurely — complete one action, immediately check next step
2. Task Completion Check — after each action, verify all fields collected, replies sent, follow-ups scheduled
3. Missing Info Protocol — identify all missing fields at once, send one inquiry, set 24h follow-up
4. Proactive Follow-up — 24h no reply → reminder | 48h → escalate | 72h → notify supervisor
5. Error Recovery — retry with alternative approach, never give up
6. Context Preservation — remember history, do not re-ask provided information`,

  // ── Document Parsers ───────────────────────────────────────────────────────
  'document-parser.mbl': `You are a Master Bill of Lading (MBL) document parser.

Extract: MBL Number, Carrier/SCAC, Vessel/Voyage, POL, POD, Shipper, Consignee, Container(s), Cargo, Weight, CBM, Freight Terms, Issue Date.

Output as JSON. Set null for missing fields. Never hallucinate.`,

  'document-parser.hbl': `You are a House Bill of Lading (HBL) document parser.

Extract: HBL Number, MBL Reference, Shipper, Consignee, Notify Party, Vessel/Voyage, POL/ETD, POD/ETA, Container(s), Cargo, Weight, CBM, Freight Terms, HS Code.

Return structured JSON. Null for missing fields.`,

  'document-parser.manifest': `You are an import cargo manifest parser.

Parse the manifest and group by MBL.
For each MBL: Container(s), Shipper, Consignee, Cargo, Weight, CBM.
Under each MBL: list all associated HBL/cargo items.

Output as JSON array of MBL objects with nested children.`,

  'document-parser.noa': `You are a Notice of Arrival (NOA) parser.

Extract: Arrival Notice Number, Issue Date, Vessel/Voyage, ETA, Port, Container(s), MBL/HBL Numbers, Shipper, Consignee, Cargo, Free Storage Days, Estimated Charges.

Flag if: ETA has changed, or charges seem unusually high.
Return JSON.`,
};

// ─── Component ─────────────────────────────────────

export default function PromptEditorPage() {
  const { t } = useTranslation();

  // State
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [activePrompt, setActivePrompt] = useState<PromptVersion | null>(null);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAssist, setShowAssist] = useState(false);

  // Prompt Assist state
  const [assistMessages, setAssistMessages] = useState<AssistMessage[]>([]);
  const [assistInput, setAssistInput] = useState('');
  const [assistLoading, setAssistLoading] = useState(false);

  // Editor state
  const [draftContent, setDraftContent] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [targetModel, setTargetModel] = useState('or-gemini-3.1-flash-lite');
  const [maxTokens, setMaxTokens] = useState(4096);
  const [temperature, setTemperature] = useState(0.3);
  const [variables, setVariables] = useState('');

  // ─── Load active prompt & versions ───────────────

  const loadPrompt = useCallback(async (key: string) => {
    setLoading(true);
    setShowHistory(false);
    try {
      const [active, vers] = await Promise.allSettled([
        api.get<PromptVersion>(`/training/prompts/${encodeURIComponent(key)}/active`),
        api.get<PromptVersion[]>(`/training/prompts/${encodeURIComponent(key)}/versions`),
      ]);

      if (active.status === 'fulfilled') {
        setActivePrompt(active.value);
        setDraftContent(active.value.content);
        setTargetModel(active.value.target_model ?? 'or-gemini-3.1-flash-lite');
        setMaxTokens(active.value.max_tokens ?? 4096);
        setTemperature(active.value.temperature ?? 0.3);
        setVariables((active.value.variables ?? []).join(', '));
      } else {
        setActivePrompt(null);
        // Pre-fill with starter template so the editor is not empty
        setDraftContent(STARTER_TEMPLATES[key] ?? '');
      }

      if (vers.status === 'fulfilled') {
        setVersions(vers.value);
      } else {
        setVersions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedKey) loadPrompt(selectedKey);
  }, [selectedKey, loadPrompt]);

  // ─── Save new version ────────────────────────────

  const handleSave = async () => {
    if (!selectedKey || !draftContent.trim()) return;
    setSaving(true);
    try {
      await api.post(`/training/prompts/${encodeURIComponent(selectedKey)}/versions`, {
        content: draftContent,
        variables: variables.split(',').map((v) => v.trim()).filter(Boolean),
        targetModel,
        maxTokens,
        temperature,
        changeNote: changeNote || undefined,
        parentId: activePrompt?.id,
      });
      setChangeNote('');
      await loadPrompt(selectedKey);
    } finally {
      setSaving(false);
    }
  };

  // ─── Activate version ────────────────────────────

  const handleActivate = async (versionId: string) => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await api.post(`/training/prompts/${encodeURIComponent(selectedKey)}/activate`, {
        versionId,
        reason: 'Activated via Prompt Editor UI',
      });
      await loadPrompt(selectedKey);
    } finally {
      setSaving(false);
    }
  };

  // ─── Rollback ────────────────────────────────────

  const handleRollback = async () => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await api.post(`/training/prompts/${encodeURIComponent(selectedKey)}/rollback`, {
        reason: 'Rollback via Prompt Editor UI',
      });
      await loadPrompt(selectedKey);
    } finally {
      setSaving(false);
    }
  };

  // ─── Prompt Assist: send message, get LLM suggestion ────

  const handleAssistSend = async () => {
    if (!assistInput.trim() || !selectedKey || assistLoading) return;
    const userMsg: AssistMessage = { role: 'user', content: assistInput.trim(), createdAt: new Date().toISOString() };
    const history = [...assistMessages, userMsg];
    setAssistMessages(history);
    setAssistInput('');
    setAssistLoading(true);
    try {
      const res = await api.post<{
        explanation: string;
        suggestedPrompt: string | null;
        changes: { type: string; description: string }[];
      }>(`/training/prompts/${encodeURIComponent(selectedKey)}/assist`, {
        message: userMsg.content,
        currentPrompt: draftContent,
        history: assistMessages.map((m) => ({ role: m.role, content: m.content })),
      });
      const changesSummary = res.changes?.length
        ? `\n\n**Changes:**\n${res.changes.map((c) => `- ${c.type}: ${c.description}`).join('\n')}`
        : '';
      const assistantMsg: AssistMessage = {
        role: 'assistant',
        content: res.explanation + changesSummary,
        suggestedPrompt: res.suggestedPrompt ?? undefined,
        applied: false,
        createdAt: new Date().toISOString(),
      };
      setAssistMessages((prev) => [...prev, assistantMsg]);
    } catch {
      setAssistMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('admin.prompt.assist.error'), createdAt: new Date().toISOString() },
      ]);
    } finally {
      setAssistLoading(false);
    }
  };

  // Apply suggested prompt into editor draft
  const applyAssistSuggestion = (msgIndex: number, suggested: string) => {
    setDraftContent(suggested);
    setAssistMessages((prev) =>
      prev.map((m, i) => (i === msgIndex ? { ...m, applied: true } : m)),
    );
    setChangeNote(t('admin.prompt.assist.appliedNote'));
  };

  // ─── Load a specific version into editor ─────────

  const loadVersionToEditor = (ver: PromptVersion) => {
    setDraftContent(ver.content);
    setTargetModel(ver.target_model ?? 'or-gemini-3.1-flash-lite');
    setMaxTokens(ver.max_tokens ?? 4096);
    setTemperature(ver.temperature ?? 0.3);
    setVariables((ver.variables ?? []).join(', '));
    setShowHistory(false);
  };

  // Filter prompt keys
  const filteredKeys = PROMPT_KEYS.filter(
    (p) =>
      p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t(p.labelKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Group filtered keys by category  
  const groupedKeys = filteredKeys.reduce<Record<string, typeof filteredKeys>>(
    (acc, p) => {
      const cat = p.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(p);
      return acc;
    },
    {},
  );

  const isDirty = activePrompt ? draftContent !== activePrompt.content : draftContent.length > 0;

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex h-full gap-4 p-4">
      {/* ── Left Panel: Prompt Key List ── */}
      <div className="w-64 shrink-0 flex flex-col gap-2">
        <Card className="flex-1 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('admin.prompt.listTitle')}
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t('admin.prompt.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {Object.entries(groupedKeys).map(([category, keys]) => (
              <div key={category} className="mb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 px-2 py-1 mt-1">
                  {category}
                </p>
                {keys.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setSelectedKey(p.key)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center gap-1',
                      selectedKey === p.key
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted text-muted-foreground',
                    )}
                  >
                    <ChevronRight className={cn('h-3 w-3 shrink-0 transition-transform', selectedKey === p.key && 'rotate-90')} />
                    <span className="truncate">{t(p.labelKey)}</span>
                  </button>
                ))}
              </div>
            ))}
            {filteredKeys.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">{t('admin.prompt.noMatch')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Right Panel: Editor ── */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {!selectedKey ? (
          <Card className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t('admin.prompt.selectHint')}</p>
            </div>
          </Card>
        ) : loading ? (
          <Card className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </Card>
        ) : (
          <>
            {/* Top bar: key info + actions */}
            <Card>
              <CardContent className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant="outline" className="text-xs font-mono shrink-0">
                    {selectedKey}
                  </Badge>
                  {activePrompt && (
                    <>
                      <Badge className="bg-green-500/10 text-green-600 text-xs">
                        v{activePrompt.version} {t('admin.prompt.active')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {t('admin.prompt.uses', { count: activePrompt.total_uses })}
                        {activePrompt.avg_quality_score != null && ` · ${t('admin.prompt.quality', { score: (activePrompt.avg_quality_score * 100).toFixed(0) })}`}
                      </span>
                    </>
                  )}
                  {!activePrompt && (
                    <>
                      <Badge variant="outline" className="text-xs text-amber-600">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {t('admin.prompt.noActiveVersion')}
                      </Badge>
                      {draftContent && (
                        <Badge className="bg-primary/10 text-primary text-xs">
                          {t('admin.prompt.starterTemplate')}
                        </Badge>
                      )}
                    </>
                  )}
                  {isDirty && (
                    <Badge className="bg-amber-500/10 text-amber-600 text-xs">{t('admin.prompt.unsavedChanges')}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowAssist(!showAssist); if (showHistory) setShowHistory(false); }}
                    className={cn(showAssist && 'bg-muted text-primary')}
                  >
                    <Bot className="h-3.5 w-3.5 mr-1" />
                    Prompt Assist
                    {assistMessages.length > 0 && (
                      <span className="ml-1 rounded-full bg-primary/20 text-primary text-[10px] px-1.5">
                        {assistMessages.length}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettings(!showSettings)}
                    className={cn(showSettings && 'bg-muted')}>
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    {t('admin.prompt.params')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowHistory(!showHistory); if (showAssist) setShowAssist(false); }}
                    className={cn(showHistory && 'bg-muted')}
                  >
                    <History className="h-3.5 w-3.5 mr-1" />
                    {t('admin.prompt.history')} ({versions.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRollback}
                    disabled={saving || !activePrompt}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    {t('admin.prompt.rollback')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !draftContent.trim()}
                  >
                    {saving ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5 mr-1" />
                    )}
                    {t('admin.prompt.saveNewVersion')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Model parameters panel (collapsible) */}
            {showSettings && (
              <Card>
                <CardContent className="py-3">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">{t('admin.prompt.targetModel')}</label>
                      <Input
                        value={targetModel}
                        onChange={(e) => setTargetModel(e.target.value)}
                        className="h-8 text-xs"
                        placeholder="gemini-3-flash-preview"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">{t('admin.prompt.maxTokens')}</label>
                      <Input
                        type="number"
                        value={maxTokens}
                        onChange={(e) => setMaxTokens(Number(e.target.value))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Temperature</label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={temperature}
                        onChange={(e) => setTemperature(Number(e.target.value))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">{t('admin.prompt.variables')}</label>
                      <Input
                        value={variables}
                        onChange={(e) => setVariables(e.target.value)}
                        className="h-8 text-xs"
                        placeholder="tenant_name, job_data"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main area: editor + optional history sidebar */}
            <div className="flex-1 flex gap-4 min-h-0">
              {/* Editor */}
              <Card className="flex-1 flex flex-col min-w-0">
                <CardContent className="flex-1 flex flex-col p-3 gap-2">
                  <textarea
                    value={draftContent}
                    onChange={(e) => setDraftContent(e.target.value)}
                    className="flex-1 w-full resize-none rounded border bg-muted/30 p-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary/50"
                    placeholder={t('admin.prompt.editorPlaceholder')}
                    spellCheck={false}
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      value={changeNote}
                      onChange={(e) => setChangeNote(e.target.value)}
                      placeholder={t('admin.prompt.changeNote')}
                      className="flex-1 h-8 text-xs"
                    />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {draftContent.length} {t('admin.prompt.chars')} · ~{Math.ceil(draftContent.length / 4)} {t('admin.prompt.tokens')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Prompt Assist chat panel */}
              {showAssist && (
                <Card className="w-80 shrink-0 flex flex-col">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs flex items-center gap-1">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      Prompt Assist
                    </CardTitle>
                    <p className="text-[10px] text-muted-foreground leading-snug">
                      Describe the change you want — AI will analyze the current prompt and suggest edits. Confirm to overwrite the draft.
                    </p>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col gap-2 p-2 min-h-0">
                    {/* Message log */}
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                      {assistMessages.length === 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          <Bot className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p className="text-[11px] leading-snug">
                            e.g.:<br />
                            “Add logic to validate container no. format”<br />
                            “Make replies more concise, remove repetition”
                          </p>
                        </div>
                      )}
                      {assistMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            'rounded-lg px-3 py-2 text-xs leading-relaxed',
                            msg.role === 'user'
                              ? 'bg-primary/10 text-primary ml-4'
                              : 'bg-muted mr-4',
                          )}
                        >
                          <div className="flex items-center gap-1 mb-1 opacity-60">
                            {msg.role === 'user' ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
                            <span className="text-[10px]">{msg.role === 'user' ? 'You' : 'Prompt Assist'}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          {msg.suggestedPrompt && (
                            <div className="mt-2 border rounded p-2 bg-background/50">
                              <p className="text-[10px] text-muted-foreground mb-1">Suggested new prompt:</p>
                              <p className="font-mono text-[10px] line-clamp-4 text-foreground/80">
                                {msg.suggestedPrompt.slice(0, 200)}{msg.suggestedPrompt.length > 200 ? '…' : ''}
                              </p>
                              <Button
                                size="sm"
                                variant={msg.applied ? 'outline' : 'default'}
                                className="h-6 text-[10px] px-2 mt-2 w-full"
                                disabled={!!msg.applied}
                                onClick={() => applyAssistSuggestion(idx, msg.suggestedPrompt!)}
                              >
                                {msg.applied ? (
                                  <><CheckCheck className="h-3 w-3 mr-1" />Applied to draft</>
                                ) : (
                                  <><CheckCircle className="h-3 w-3 mr-1" />Apply to editor</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      {assistLoading && (
                        <div className="bg-muted rounded-lg px-3 py-2 mr-4 flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Analysing prompt...
                        </div>
                      )}
                    </div>
                    {/* Input */}
                    <div className="flex gap-1 pt-1">
                      <textarea
                        value={assistInput}
                        onChange={(e) => setAssistInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAssistSend(); }
                        }}
                        placeholder="Describe the change… (Enter to send)"
                        className="flex-1 resize-none rounded border bg-muted/30 p-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/50 min-h-[56px] max-h-24"
                      />
                      <Button
                        size="sm"
                        className="h-auto px-2 self-end mb-0"
                        disabled={!assistInput.trim() || assistLoading}
                        onClick={handleAssistSend}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Version history sidebar */}
              {showHistory && (
                <Card className="w-72 shrink-0 flex flex-col">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs flex items-center gap-1">
                      <History className="h-3.5 w-3.5" />
                      {t('admin.prompt.versionHistory')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-y-auto p-2 space-y-1">
                    {versions.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        {t('admin.prompt.noVersions')}
                      </p>
                    ) : (
                      versions.map((ver) => (
                        <div
                          key={ver.id}
                          className={cn(
                            'rounded border p-2 text-xs space-y-1',
                            ver.status === 'active'
                              ? 'border-green-500/30 bg-green-500/5'
                              : 'border-border',
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">v{ver.version}</span>
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px]',
                                ver.status === 'active' && 'text-green-600',
                                ver.status === 'draft' && 'text-blue-600',
                                ver.status === 'archived' && 'text-muted-foreground',
                              )}
                            >
                              {ver.status === 'active' ? t('admin.prompt.active') : ver.status === 'draft' ? t('admin.prompt.draft') : t('admin.prompt.archived')}
                            </Badge>
                          </div>
                          {ver.change_note && (
                            <p className="text-muted-foreground truncate">{ver.change_note}</p>
                          )}
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {new Date(ver.created_at).toLocaleDateString('en-SG')}
                            {ver.total_uses > 0 && <span> · {ver.total_uses} uses</span>}
                          </div>
                          <div className="flex gap-1 pt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-[10px] px-2"
                              onClick={() => loadVersionToEditor(ver)}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              {t('admin.prompt.load')}
                            </Button>
                            {ver.status !== 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] px-2 text-green-600"
                                onClick={() => handleActivate(ver.id)}
                                disabled={saving}
                              >
                                <Play className="h-3 w-3 mr-1" />
                                {t('admin.prompt.activate')}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
